/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../utils/configurationProvider";
import ICONS from "../../../assets/Icons";
import SVGComponent from "../SVGComponent";
import IconRoundButton from "../CommonButtons/IconRoundButton";
import Loader from "../../Loader/Index";
import PrimaryButton from "../CommonButtons/PrimaryButton";
import { getUpdateInformation } from "../../../redux/features/sharedResources/sharedResources";

/**
 * Edit history popup. Fetches its own data on mount; the parent only controls whether it is rendered.
 *
 * @param {Object} props
 * @param {string} [props.subtext] - Optional text below the header
 * @param {() => void} props.closehandleFunction - Called after the close animation
 * @param {boolean} [props.closeisDisabled] - Disable the close button
 * @param {string} [props.closedisabledBgColor] - Close button background when disabled
 * @param {React.RefObject} [props.closebuttonRef] - Ref for the close button
 * @param {string} [props.closeimgColor] - Close icon color
 * @param {string} props.resourceType - e.g. "beneficiary_accounts" | "internal_accounts"
 * @param {string} props.resourceId - id of the record whose history is fetched
 */

// updated_at is a Unix timestamp in seconds -> "13 Oct 2026, 9:42 am"
const formatDate = (timestamp) => {
  if (!timestamp) return "Not available";

  const d = new Date(timestamp * 1000);
  if (isNaN(d.getTime())) return "Not available";

  const date = d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  const time = d
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();

  return `${date}, ${time}`;
};

const getOrdinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const formatKey = (key = "") => {
  if (["pan", "gstin"].includes(key)) return key.toUpperCase();
  const text = key.replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const formatValue = (value) => {
  //   if (value === null || value === undefined || value === "") return "Not given";
  //   if (typeof value === "boolean") return value ? "Yes" : "No";
  //   if (
  //     typeof value === "string" &&
  //     /^\d{4}-\d{2}-\d{2}T/.test(value) &&
  //     !isNaN(new Date(value).getTime())
  //   ) {
  //     return formatDate(new Date(value).getTime() / 1000);
  //   }
  return String(value);
};

const EditHistoryItem = ({ change, isOpen, onToggle }) => {
  const rows = change.specific_changes || [];

  return (
    <div className="flex flex-col">
      <div
        onClick={onToggle}
        className={`flex justify-between items-center gap-gap-button-l relative z-z-index-base p-padding-md bg-primary-card global-hover-white border border-primary-act cursor-pointer transition-[border-radius] duration-animation-fast ${
          isOpen
            ? "rounded-b-none rounded-t-border-radius-md !border-b-border-base transition-none"
            : "rounded-border-radius-md delay-animation-fast-m"
        }`}
      >
        <div className="text-text-primary text-font-sm font-semibold text-center">
          {getOrdinal(change.editNumber)} edit - {formatDate(change.updated_at)}
        </div>
        <div
          className={`transition-transform duration-animation-fast ease-in-out w-icon-sm h-icon-sm ${
            isOpen ? "rotate-half" : "rotate-base"
          }`}
        >
          <SVGComponent
            url={ICONS?.ARROW_DOWN_WHITE}
            color="var(--text-primary)"
            width="var(--icon-sm)"
            height="var(--icon-sm)"
          />
        </div>
      </div>

      <div
        className="grid transition-[grid-template-rows] duration-animation-fast ease-in-out"
        style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="flex flex-col bg-primary-card overflow-hidden border !border-t-border-base border-primary-act rounded-b-border-radius-md rounded-t-none">
            <div className="grid grid-cols-2 text-text-primary text-font-xs text-center bg-primary-nav">
              <div className="py-padding-sm px-padding-md border-r border-primary-card">
                Old information
              </div>
              <div className="py-padding-sm px-padding-md">
                Updated information
              </div>
            </div>

            <div className="flex flex-col mx-margin-md">
              {rows.map((item, i) => (
                <div
                  key={`${item.key}-${i}`}
                  className={`grid grid-cols-2 ${
                    i !== rows.length - 1 ? "border-b border-primary-bg" : ""
                  }`}
                >
                  <div className="flex flex-col gap-gap-xs min-w-0 pr-padding-md py-padding-sm">
                    <span className="text-text-primary text-font-sm break-words">
                      {formatValue(item.old_value)}
                    </span>
                    <span className="text-text-secondary text-font-xs">
                      {formatKey(item.key)}
                    </span>
                  </div>

                  <div className="flex flex-col gap-gap-xs min-w-0 pl-padding-md py-padding-sm">
                    <span className="text-text-primary text-font-sm break-words">
                      {formatValue(item.new_value)}
                    </span>
                    <span className="text-text-secondary text-font-xs">
                      {formatKey(item.key)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CommonEditHistoryPopUp = ({
  subtext,
  closehandleFunction,
  closeisDisabled,
  closedisabledBgColor,
  closebuttonRef,
  closeimgColor,
  resourceType,
  resourceId,
}) => {
  const dispatch = useDispatch();

  const [animate, setAnimate] = useState(false);
  const [isError, setIsError] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editHistoryData, setEditHistoryData] = useState(null);

  const fetchEditHistory = async () => {
    setIsLoading(true);
    setIsError(false);
    setEditHistoryData(null);

    try {
      const res = await dispatch(
        getUpdateInformation({
          resource_type: resourceType,
          resource_id: resourceId,
        }),
      );

      if (getUpdateInformation.fulfilled.match(res)) {
        setEditHistoryData(res.payload);
      } else {
        const errMsg = res?.payload?.errors?.base?.[0] || "";

        if (errMsg.toLowerCase().includes("no update history found")) {
          setEditHistoryData({ status: true, total_count: 0, changes: [] });
        } else {
          setIsError(true);
        }
      }
    } catch (error) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!resourceType || !resourceId) return;
    fetchEditHistory();
  }, [resourceType, resourceId]);

  const sortedChanges = [...(editHistoryData?.changes || [])]
    .sort((a, b) => a.updated_at - b.updated_at)
    .map((change, i) => ({ ...change, editNumber: i + 1 }))
    .reverse();

  return (
    <div className="fixed top-0 left-0 w-screen h-screen bg-PopUp-BackDrop bg-opacity-dark-opacity z-z-index-modal">
      <div
        className="flex w-full h-screen justify-center items-center m-auto top-top-half left-left-half fixed z-z-index-modal p-padding-sm"
        style={{ transform: "translate(-50%, -50%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`overflow-hidden w-full Qmd:w-small-popup-width flex flex-col bg-primary-bg rounded-border-radius-md animate-none max-h-alert-popup-height-2 ${
            animate
              ? "Qmd:animate-slideInFromLeft"
              : "Qmd:animate-slideInFromRight"
          }`}
        >
          <div className="h-full flex flex-col flex-1 overflow-hidden">
            <div className="flex flex-col gap-gap-xs px-padding-md pt-padding-md">
              <div className="flex justify-between gap-gap-md">
                <div className="flex flex-col gap-gap-xs">
                  <p className="text-text-primary text-font-md font-semibold">
                    Edit history (
                    {isLoading
                      ? GlobalConfiguration.lengthCheckZero
                      : (editHistoryData?.changes?.length ??
                        GlobalConfiguration.lengthCheckZero)}
                    )
                  </p>
                  <p className="text-text-primary text-font-sm">
                    Following is the history of changes made to this account.
                  </p>
                </div>
                <IconRoundButton
                  enabledBgColor="var(--primary-nav)"
                  isLoading={false}
                  handleFunction={() => {
                    if (!closeisDisabled) {
                      setAnimate(true);
                      setTimeout(() => {
                        closehandleFunction();
                      }, IntervalConfiguration.intervalSemiMid);
                    }
                  }}
                  title="Close"
                  icon={ICONS?.CLOSE}
                  hoverOption="white"
                  btnType="button"
                  isDisabled={closeisDisabled}
                  disabledBgColor={closedisabledBgColor}
                  buttonRef={closebuttonRef}
                  imgColor={closeimgColor}
                />
              </div>
            </div>

            <div className="w-full h-full mt-margin-md px-padding-md flex flex-col gap-gap-md mb-margin-md flex-1 overflow-y-scroll">
              {isLoading ? (
                <div className="flex-1 flex items-center justify-center my-margin-xl">
                  <Loader height="var(--loader-md)" width="var(--loader-md)" />
                </div>
              ) : isError ? (
                <div className="flex-1 flex flex-col items-center justify-center gap-gap-sm my-margin-xl">
                  <p className="text-text-primary text-font-xs text-center">
                    An error occurred while fetching the edit history. Try again
                  </p>
                  <PrimaryButton
                    btnText="Fetch again"
                    btnType="button"
                    enabledBgColor="var(--primary-nav-act)"
                    enabledTextColor="var(--text-primary)"
                    hoverOption="white"
                    handleFunction={fetchEditHistory}
                  />
                </div>
              ) : editHistoryData?.total_count ===
                GlobalConfiguration.lengthCheckZero ? (
                <div className="flex-1 flex flex-col items-center justify-center gap-gap-sm my-margin-xl">
                  <p className="text-text-primary text-font-xs text-center">
                    Since the account number has been updated, the updated
                    <br /> account is treated as a new account, and the previous
                    account <br /> is archived.
                  </p>
                </div>
              ) : (
                sortedChanges.map((change, idx) => (
                  <EditHistoryItem
                    key={`${change.updated_at}-${idx}`}
                    change={change}
                    isOpen={openIndex === idx}
                    onToggle={() =>
                      setOpenIndex(openIndex === idx ? null : idx)
                    }
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonEditHistoryPopUp;
